import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  AcquisitionOperatorSignalWithEligibility,
  AcquisitionOperatorSource,
  AcquisitionOperatorStatus,
} from '@/lib/marketing/operator-queue';

export interface AcquisitionSignalFilters {
  status?: AcquisitionOperatorStatus;
  source?: AcquisitionOperatorSource;
  limit?: number;
}

interface AcquisitionSignalsResponse {
  success: true;
  signals: AcquisitionOperatorSignalWithEligibility[];
  page: {
    returned: number;
    fetched: number;
    hasMore: boolean;
    nextBefore: string | null;
  };
  safety: {
    mode: 'shadow-operator-only';
    liveWrite: false;
    liveSend: false;
    privacyCheckedAt: string;
  };
}

export interface UpdateAcquisitionSignalInput {
  id: string;
  status: AcquisitionOperatorStatus;
  note?: string | null;
}

export interface RevealedAcquisitionSignal {
  id: string;
  linkedinUrl: string;
  privacyCheckedAt: string;
}

const accessToken = async (): Promise<string> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');
  return session.access_token;
};

const apiError = async (response: Response): Promise<Error> => {
  const body = await response.json().catch(() => ({})) as {
    error?: { message?: string };
    message?: string;
  };
  return new Error(body.error?.message ?? body.message ?? `API error: ${response.status}`);
};

export const useAcquisitionSignals = (filters: AcquisitionSignalFilters = {}) => {
  return useQuery<AcquisitionSignalsResponse, Error>({
    queryKey: ['acquisition-signals', filters.status, filters.source, filters.limit],
    queryFn: async () => {
      const token = await accessToken();
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.source) params.set('source', filters.source);
      params.set('limit', String(filters.limit ?? 50));

      const response = await fetch(`/api/marketing/acquisition-signals?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw await apiError(response);
      return response.json() as Promise<AcquisitionSignalsResponse>;
    },
    staleTime: 30_000,
  });
};

export const useUpdateAcquisitionSignal = () => {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, UpdateAcquisitionSignalInput>({
    mutationFn: async (input) => {
      const token = await accessToken();
      const response = await fetch('/api/marketing/acquisition-signals', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(input),
      });
      if (!response.ok) throw await apiError(response);
      return response.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['acquisition-signals'] });
    },
  });
};

/**
 * One-shot action-time reveal. The list response never carries a LinkedIn URL;
 * every copy/open click obtains a fresh accepted-provenance and privacy proof.
 */
export const revealAcquisitionSignal = async (
  id: string,
): Promise<RevealedAcquisitionSignal> => {
  const token = await accessToken();
  const response = await fetch('/api/marketing/acquisition-signals', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ id }),
  });
  if (!response.ok) throw await apiError(response);
  const body = await response.json() as {
    success?: unknown;
    signal?: { id?: unknown; linkedinUrl?: unknown; privacyCheckedAt?: unknown };
  };
  const signal = body.signal;
  if (
    body.success !== true ||
    signal?.id !== id ||
    typeof signal.linkedinUrl !== 'string' ||
    !/^https:\/\/www\.linkedin\.com\/in\/[a-z0-9_-]+$/.test(signal.linkedinUrl) ||
    typeof signal.privacyCheckedAt !== 'string' ||
    !Number.isFinite(new Date(signal.privacyCheckedAt).getTime())
  ) {
    throw new Error('Manual outreach authorization is unavailable');
  }
  return signal as RevealedAcquisitionSignal;
};
